import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Zap, Globe, User, ShieldCheck, Calendar, BookOpen, TrendingUp, ArrowRight, Star } from "lucide-react";
import Navbar from "@/components/Navbar";

const SCHEMA = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Lakshmi AI Features",
    "description": "Complete feature list of Lakshmi — AI LinkedIn post writer and personal brand tool for Indian professionals",
    "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Multi-platform content generation", "description": "Write LinkedIn posts, X threads, Instagram captions and Reddit posts from one prompt." },
        { "@type": "ListItem", "position": 2, "name": "Brand voice matching", "description": "Learns your writing style from a sample post and applies it to every draft." },
        { "@type": "ListItem", "position": 3, "name": "4-agent AI pipeline", "description": "Strategist, Voice Coach, Ghostwriter, and QA Editor work in sequence." },
        { "@type": "ListItem", "position": 4, "name": "Content calendar", "description": "Schedule posts across platforms with a built-in calendar." },
        { "@type": "ListItem", "position": 5, "name": "AI quality scoring", "description": "Every post gets a quality score and feedback before you publish." },
        { "@type": "ListItem", "position": 6, "name": "Source ingestion", "description": "Paste an article URL or PDF — Lakshmi turns it into post ideas." },
    ]
};

const FEATURES = [
    {
        icon: Globe, title: "Write for 4 platforms simultaneously",
        body: "One idea → LinkedIn post, X thread, Instagram caption, Reddit post. Platform-native tone and format for each. No copy-paste editing.",
        tag: "LinkedIn · X · Instagram · Reddit",
    },
    {
        icon: User, title: "Brand voice that sounds like you",
        body: "Paste one post you're proud of. Lakshmi analyses your rhythm, vocabulary, and sentence patterns — and enforces them on every future draft.",
        tag: "Voice matching",
    },
    {
        icon: Zap, title: "4-agent AI pipeline",
        body: "Content Strategist finds the angle. Voice Coach calibrates the tone. Ghostwriter drafts the post. QA Editor scores it before delivery.",
        tag: "Multi-agent AI",
    },
    {
        icon: ShieldCheck, title: "Quality score on every post",
        body: "Every draft gets scored 0–100. Hook strength, story quality, CTA clarity, voice match — scored automatically. Retried if below threshold.",
        tag: "AI quality gate",
    },
    {
        icon: Calendar, title: "Content calendar & scheduling",
        body: "Plan your content weeks in advance. Schedule posts to a calendar. See your publishing cadence at a glance.",
        tag: "Scheduling",
    },
    {
        icon: BookOpen, title: "Source ingestion",
        body: "Paste an article URL, blog post, or PDF. Lakshmi extracts key ideas and generates post angles from real content — not hallucinated.",
        tag: "URL & PDF import",
    },
    {
        icon: TrendingUp, title: "Analytics & performance tracking",
        body: "See how your posts perform. Track quality scores over time. Understand which topics and angles work best for your audience.",
        tag: "Analytics",
    },
    {
        icon: Sparkles, title: "AI blog writer",
        body: "Generate long-form blog posts with structured outlines, multiple sections, and SEO-friendly formatting — from a single topic prompt.",
        tag: "Blog generation",
    },
];

export default function Features() {
    useEffect(() => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.text = JSON.stringify(SCHEMA);
        document.head.appendChild(s);
        document.title = "Features — Lakshmi AI LinkedIn Post Writer & Personal Brand Tool";
        return () => s.remove();
    }, []);

    return (
        <div className="min-h-screen">
            <Navbar />

            {/* Hero with character image */}
            <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-8">
                <div className="flex flex-col lg:flex-row items-center gap-12 mb-20">
                    <div className="flex-1 text-center lg:text-left">
                        <div className="lk-label mb-3">Everything Lakshmi can do</div>
                        <h1 className="font-heading text-4xl sm:text-6xl tracking-tight mb-4">
                            Features built for <span className="lk-gradient-text">personal brand builders</span>
                        </h1>
                        <p className="text-[var(--lk-muted)] text-base sm:text-lg max-w-2xl mb-8">
                            Lakshmi is the only AI writing tool purpose-built for Indian professionals who want to grow on LinkedIn, X, Instagram and Reddit — without sounding like a bot.
                        </p>
                        <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                            <Link to="/signup" className="lk-btn-primary rounded-xl px-8 py-4 inline-flex items-center gap-2 text-sm">
                                Try all features free <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                        {/* Social proof strip */}
                        <div className="mt-6 flex items-center gap-2 justify-center lg:justify-start">
                            {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
                            <span className="text-sm text-[var(--lk-muted)] ml-1">Loved by 500+ Indian professionals</span>
                        </div>
                    </div>
                    {/* Character + mock post card */}
                    <div className="relative flex-shrink-0 w-full max-w-sm" style={{ isolation: "isolate" }}>
                        <picture>
                            <source srcSet="/image/lakshmi_nobg.webp" type="image/webp" />
                            <img src="/image/lakshmi_nobg_opt.png" alt="Lakshmi AI assistant"
                                className="w-64 sm:w-80 mx-auto lk-float relative z-0" loading="lazy"
                                style={{ filter: "drop-shadow(0 0 40px rgba(124,58,237,0.4))" }} />
                        </picture>
                        {/* Floating mock post card */}
                        <div className="absolute top-8 -left-4 sm:-left-8 lk-border lk-surface rounded-xl p-4 w-52 shadow-xl text-xs z-10">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-6 h-6 rounded-full bg-violet-600 flex items-center justify-center text-white font-bold text-xs">A</div>
                                <div>
                                    <div className="font-semibold text-xs">Arjun Mehta</div>
                                    <div className="text-[var(--lk-muted)] text-xs">Software Engineer, Bengaluru</div>
                                </div>
                            </div>
                            <p className="text-[var(--lk-muted)] leading-snug">3 years ago I was afraid to post on LinkedIn. Today I have 12K followers. Here's what changed…</p>
                            <div className="mt-2 flex gap-3 text-[var(--lk-muted)]">
                                <span>👍 842</span><span>💬 94</span>
                            </div>
                            <div className="mt-1.5 px-2 py-0.5 rounded-full text-xs inline-block font-medium"
                                style={{ background: "rgba(52,211,153,0.15)", color: "#34D399" }}>
                                Quality score: 94/100
                            </div>
                        </div>
                        {/* Speed badge */}
                        <div className="absolute bottom-16 -right-4 sm:-right-8 lk-border lk-surface rounded-xl px-3 py-2 shadow-xl text-xs font-semibold flex items-center gap-2 z-10"
                            style={{ borderColor: "rgba(124,58,237,0.4)" }}>
                            <Zap className="w-3.5 h-3.5 text-[var(--lk-primary)]" />
                            <span>Generated in 47s</span>
                        </div>
                    </div>
                </div>

            <div className="text-center mb-12">
                <div className="lk-label mb-2">8 powerful features</div>
                <h2 className="font-heading text-2xl sm:text-4xl tracking-tight">Everything you need to build your brand</h2>
            </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
                    {FEATURES.map((f, i) => (
                        <motion.div
                            key={f.title}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.06 }}
                            viewport={{ once: true }}
                            className="lk-border lk-surface rounded-2xl p-6 flex flex-col"
                        >
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                                style={{ background: "rgba(124,58,237,0.12)", border: "1px solid rgba(124,58,237,0.25)" }}>
                                <f.icon className="w-5 h-5 text-[var(--lk-primary)]" />
                            </div>
                            <h2 className="font-semibold text-base mb-2">{f.title}</h2>
                            <p className="text-sm text-[var(--lk-muted)] leading-relaxed flex-1">{f.body}</p>
                            <div className="mt-4 text-xs font-medium text-[var(--lk-primary)]">{f.tag}</div>
                        </motion.div>
                    ))}
                </div>

                {/* Platform logos strip */}
                <div className="lk-border lk-surface rounded-2xl p-6 mb-10">
                    <div className="text-center text-sm text-[var(--lk-muted)] mb-5 font-medium">Write for all platforms from one prompt</div>
                    <div className="flex flex-wrap justify-center gap-6">
                        {[
                            { label: "LinkedIn", color: "#0A66C2", letter: "in" },
                            { label: "X / Twitter", color: "#fff", letter: "X" },
                            { label: "Instagram", color: "#E4405F", letter: "IG" },
                            { label: "Reddit", color: "#FF4500", letter: "r/" },
                        ].map(p => (
                            <div key={p.label} className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black"
                                    style={{ background: `${p.color}22`, color: p.color, border: `1px solid ${p.color}44` }}>
                                    {p.letter}
                                </div>
                                <span className="text-sm font-medium">{p.label}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="text-center">
                    <Link to="/signup" className="lk-btn-primary rounded-xl px-8 py-4 inline-flex items-center gap-2 text-sm">
                        Try all features free <ArrowRight className="w-4 h-4" />
                    </Link>
                    <p className="text-xs text-[var(--lk-muted)] mt-3">No credit card required.</p>
                </div>
            </div>
        </div>
    );
}
